import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faAngleUp, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { Container, Box, Button, Typography, TextField } from '@mui/material';
import debounce from 'lodash-es/debounce';

import NewSalesResultsTable from './NewSalesResultsTable';
import OpenDisputesButton from './OpenDisputesButton';
import GrandTotalsTable from './GrandTotalsTable';
import SendMessageModal from './SendMessageModal';
import SubmitFeedbackButton from '../Feedback/SubmitFeedbackButton';
import FeedbackModal from '../Feedback/FeedbackModal';
import Page from '../../Page';
import Select from '../../../ui/Select';

import { fetchSessionResults } from '../../../modules/reports/thunks';
import { fetchAndDownloadReport } from './helpers';
import { options } from './constants'
import { get } from 'lodash-es';

const SaleResults = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { houseRef: urlHouseRef, catalogRefs: urlCatalogRefs } = useParams();

  const [isOpenSendMessageModal, setIsOpenSendMessageModal] = useState(false);
  const [isOpenFeedbackModal, setIsOpenFeedbackModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState('all');
  const [bidderName, setBidderName] = useState('');
  const [userRef, setUserRef] = useState('');
  const [searchString, setSearchString]= useState('');
  const [showSalesResultsDetails, setShowSalesResultsDetails] = useState(true);
  const [currentFeedbackLotRef, setCurrentFeedbackLotRef] = useState('')
  const [canAddDispute, setCanAddDispute] = useState(false);
  const [saleDateValue, setSaleDateValue] = useState('');
  const [isButtonClickType, setIsButtonClickType] = useState('');
  const [creditsArray, setCreditsArray] = useState([]);
  const [catalogHeaderMap, setCatalogHeaderMap] = useState({});
  const reduxHouseRef = useSelector(state => state.house.houseRef);
  const houseRef = reduxHouseRef || urlHouseRef;
  const user = useSelector(state => state.user);
  const { grandTotals, catalogHeaders, memberSums, catalogs } =
    useSelector(state => state.reports.sessionResults) || {};
  const isLoadingSessionResults = useSelector(state => state.reports.isLoadingSessionResults);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [loadingFeedbackModalData, setLoadingFeedbackModalData] = useState(false);

  const [expandedFlags, setExpandedFlags] = useState({});
  const [sorting, setSorting] = useState([]);
  const [randomNumber, setRandomNumber] = useState(0);
  const [checkboxArray, setCheckboxArray] = useState([]);
  const [memberData, setMemberData] = useState([]);
  const [filteredMemberData, setFilteredMemberData] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState('$');

  const selectedRefs = urlCatalogRefs || location.state?.selectedRefs || '';

  const disputeHelpURL = 'https://www.invaluableauctions.com/inv/help-center/resource/disputes/';

  const sessionCount = catalogHeaders && catalogHeaders.length;
  const isSuperUser = user.isSuperUser;

  const { selectedLotsForDisputes } = useSelector(state => state.reports);

  const getCreditsData = async () => {
    const response = await fetch(`/app/house/${houseRef}/dispute/credits?catalogRefs=${selectedRefs}`, {
      headers: { Authorization: `${houseRef}*` },
    });
    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }
    const data = await response.json();
    if (typeof data === 'undefined') {
      return;
    }
    const creditArray = get(data, 'data.credits', []);
    setCreditsArray(creditArray);
  };

  useEffect(() => {
    getCreditsData();
  }, []);

  useEffect(() => {
    if (typeof catalogHeaders === 'undefined') {
      return;
    }
    const catalogMap = {};
    catalogHeaders.forEach(item => {
      catalogMap[item.catalogRef] = item.title;
    });

    setCatalogHeaderMap(catalogMap);
  }, [catalogHeaders]);

  useEffect(() => {
    if (catalogHeaders && catalogHeaders.length) {
      const secsInDay = 1000 * 60 * 60 * 24;
      const saleDatePart = catalogHeaders[0].date.split(' ')[0];
      const saleDate = new Date(saleDatePart);
      const today = new Date();
      const diffSecs = today.getTime() - saleDate.getTime();
      const diff = diffSecs / secsInDay;
      setCanAddDispute(isSuperUser || (diff < 50 && diff > 4));
      setSaleDateValue(saleDate.getTime())
    }
  }, [catalogHeaders, isSuperUser])

  useEffect(() => {
    if (houseRef && selectedRefs?.length > 0) {
      dispatch(fetchSessionResults(houseRef, selectedRefs.split(',')));
    }
  }, [houseRef, selectedRefs, randomNumber, dispatch]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted && houseRef && selectedRefs?.length > 0) {
        dispatch(fetchSessionResults(houseRef, selectedRefs.split(',')));
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [houseRef, selectedRefs, dispatch]);

  useEffect(() => {
    if (!memberSums || !catalogs) return;
    const expandedObject = {};
    Object.keys(memberSums).forEach(key => {
      expandedObject[key] = true;
    });
    setExpandedFlags(expandedObject);

    setCurrencySymbol(catalogs[0]?.currencySymbol);

    // memberData
    const arr = [];
    Object.keys(memberSums).forEach((key, index) => {
      const tempObj = { ...memberSums[key] };
      const memberCatalogs = catalogs.filter(catalog => catalog.memberRef === key);
      if (memberCatalogs.length > 0) {
        tempObj.firstName = memberCatalogs[0].firstName;
        tempObj.lastName = memberCatalogs[0].lastName;
        tempObj.fullName = `${memberCatalogs[0].lastName} ${memberCatalogs[0].firstName}`
        tempObj.channel = memberCatalogs[0].channel;
        tempObj.paddleNumber = memberCatalogs[0].paddleNumber;
        tempObj.bidderAddress = memberCatalogs[0].bidderAddress;
        tempObj.phone = memberCatalogs[0].phone;
        tempObj.mobilePhone = memberCatalogs[0].mobilePhone;
        tempObj.email = memberCatalogs[0].email;
        tempObj.memberRef = memberCatalogs[0].memberRef;
        tempObj.id = memberCatalogs[0].memberRef;
        tempObj.lots = memberCatalogs;
        tempObj.rowIndex = index;
        arr.push(tempObj);
      }
    });
    // sort by name
    arr.sort(function (a, b) {
      if (a.lastName.toLowerCase() < b.lastName.toLowerCase()) {
        return -1;
      }
      if (a.lastName.toLowerCase() > b.lastName.toLowerCase()) {
        return 1;
      }
      return 0;
    });
    setMemberData(arr);
  }, [memberSums, catalogs]);

  useEffect(() => {
    (isLoadingSessionResults || !selectedRefs.length) && setIsPageLoading(false);
  }, [isLoadingSessionResults, selectedRefs]);

  const closeSendMessageModal = () => {
    setIsOpenSendMessageModal(false);
  };

  const fetchFormatForSpreadSheet = async (houseRef, catalogRef) => {
    const url = `/app/house/${houseRef}/sessionReports?catalogRefs=${catalogRef}`;
    await fetchAndDownloadReport(url, 'sessionResults.xls');
  };

  const fetchBidderReport = async (houseRef, catalogRef) => {
    const url = `/app/house/${houseRef}/bidderReports?catalogRef=${catalogRef}`;
    await fetchAndDownloadReport(url, 'bidderResults.xls');
  };

  const fetchEOAReport = async (houseRef, catalogRef) => {
    const url = `/app/house/${houseRef}/eoaReports?catalogRef=${catalogRef}`;
    await fetchAndDownloadReport(url, 'eoaResults.xls');
  };

  const fetchPACTSReport = async (houseRef, catalogRef) => {
    const url = `/app/house/${houseRef}/pactsReports?catalogRef=${catalogRef}`;
    await fetchAndDownloadReport(url, 'pactsResults.xls');
  };

  const handleSearch = useCallback((event) => {
    const debouncedSearch = debounce((val) => {
      setSearchString(val.trim().toLowerCase());
    }, 500);
    debouncedSearch(event.target.value);
  }, []);

  const getNeedsFeedbackMembers = useCallback((members) => {
    return members.filter(member => member.lots.some(lot => !lot.feedbackExistsForLot));
  }, []);

  useEffect(() => {
    const baseData = selectedOption === 'withLot'
      ? getNeedsFeedbackMembers(memberData)
      : memberData;

    if (searchString === '') {
      setFilteredMemberData(baseData);
      return;
    }

    const result = baseData.filter(bidder => bidder.firstName.toLowerCase().includes(searchString)
      || bidder.lastName.toLowerCase().includes(searchString)
      || bidder.email.toLowerCase().includes(searchString));
    setFilteredMemberData(result);
  }, [searchString, memberData, selectedOption, getNeedsFeedbackMembers]);


  const toggleSalesResultsDetails = () => {
    const data = expandedFlags;
    const newValue = !showSalesResultsDetails;
    Object.keys(data).forEach(v => data[v] = newValue);
    setExpandedFlags(data);
    setShowSalesResultsDetails(newValue);
  };

  const stickyBar = () =>  {
    const searchRow = document.getElementById("searchRow");
    let stickyPos = 0;

    if (searchRow !== null) {
      stickyPos = searchRow.offsetTop;
      if (window.pageYOffset > stickyPos) {
        searchRow.classList.add("sticky")
      } else {
        searchRow.classList.remove("sticky");
      }
    }
  }

  window.onscroll = function() { stickyBar() };

  const handleFilterChange = ({value}) => {
    setSelectedOption(value);

    if (value === 'all') {
      setSearchString('');
    }
  };

  const handleClearSearch = () => {
    setSearchString('');
  };

  const canAddFeedback = (saleDate) => {
    // is today's date < 180 days after catalogDate
    const today = new Date();
    const secsInDay = 1000 * 60 * 60 * 24;
    const diffSecs = today.getTime() - saleDate;
    const diff = diffSecs / secsInDay;
    return (diff < 180);
  };

  const clearSelectedLots = () => {
     setCheckboxArray([]);
  };

  return (
    <Page header={{ title: 'Sale Results' }}>
      <Container disableGutters maxWidth={false} className="sales-results-main">
        {isPageLoading || isLoadingSessionResults ? (
          <ReactLoading className="loading-spinner" type="spin" height="auto" color="black" />
          ) : memberData?.length ? (
          <Box>
            <SendMessageModal
              isOpen={isOpenSendMessageModal}
              onClose={closeSendMessageModal}
              bidderName={bidderName}
              userRef={userRef}
              houseRef={houseRef}
            />
            <Box>
              <Typography variant="body2" mb={1}>
                On this screen, you can track the results of your sales, leave feedback on bidders,
                or file disputes on non-paying bidders. You can open a dispute starting at 4 days
                and up to 50 days after your sale.
                <a
                  className="link"
                  href={disputeHelpURL}
                  target="_blank"
                  rel="noreferrer"
                  style={{ cursor: 'pointer' }}
                >
                  {' '}
                  Learn more about managing disputes and returns »
                </a>
              </Typography>
            </Box>
            {isOpenFeedbackModal &&
              <Box>
                <FeedbackModal
                  isOpen={isOpenFeedbackModal}
                  onClose={() => setIsOpenFeedbackModal(false)}
                  bidderName={bidderName}
                  currentFeedbackLotRef={currentFeedbackLotRef}
                  clickType={isButtonClickType}
                  setIsButtonClickType={setIsButtonClickType}
                  loadingFeedbackModalData={loadingFeedbackModalData}
                  setLoadingFeedbackModalData={setLoadingFeedbackModalData}
                  setRandomNumber={setRandomNumber}
                  checkboxArray={checkboxArray}
                />
              </Box>
            }
            {searchString === '' && (
              <Box className="mt-4 mb-4 border-top border-bottom py-2">
               <GrandTotalsTable grandTotals={grandTotals} currencySymbol={currencySymbol} />
              </Box>
            )}
            <Box sx={{ mb: 1 }}>
              {catalogHeaders?.map(header => (
                <div key={header.catalogRef} style={{ fontWeight: 'bold', fontSize: '14px' }}>
                  {sessionCount > 1
                    ? `Session ${header.prefix}: ${header.title} - ${header.date.split(' ')[0]}`
                    : `${header.title} - ${header.date.split(' ')[0]}`}
                </div>
              ))}
            </Box>
            <Box className="inline mt-20">
              <Button
                variant="text"
                color="secondary"
                size="sm"
                className="mr-2"
                onClick={() => fetchFormatForSpreadSheet(houseRef, selectedRefs)}
              >
                Format for spreadsheet
              </Button>
              {sessionCount === 1 ? (
                <>
                  <span> | </span>
                  <Button
                    variant="text"
                    color="secondary"
                    size="sm"
                    className="mx-2"
                    onClick={() => fetchBidderReport(houseRef, selectedRefs)}
                  >
                    Bidder Report
                  </Button>
                  <span> | </span>
                  <Button
                    variant="text"
                    color="secondary"
                    size="sm"
                    className="mx-2"
                    onClick={() => fetchEOAReport(houseRef, selectedRefs)}
                  >
                    EOA Report
                  </Button>
                  <span> | </span>
                  <Button
                    variant="text"
                    color="secondary"
                    size="sm"
                    className="mx-2"
                    onClick={() => fetchPACTSReport(houseRef, selectedRefs)}
                  >
                    PACTS Report
                  </Button>
                </>
              ) : null}
            </Box>
            <Box id="searchRow" sx={{ mt: 2, mb: 0 }} display="flex" justifyContent="space-between">
            <Box width={selectedLotsForDisputes.length > 0 ? '350px' : '400px'} mb={2}>
              <Typography variant="body2" fontWeight="bold">
                Search:
              </Typography>
              <Box display="flex" alignItems="center">
                  <TextField
                    placeholder="Please enter name or email"
                    className="textField"
                    id="outlined"
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <Box m={0} className="search-field-icon">
                          <FontAwesomeIcon
                            icon="search"
                            className="small"
                            style={{ marginLeft: '10px' }}
                          />
                        </Box>
                      ),
                      endAdornment:
                        searchString !== '' ? (
                          <Box mr={1} onClick={() => handleClearSearch()}>
                            <FontAwesomeIcon
                              icon={faCircleXmark}
                              className="color-gray-bg small"
                              style={{ cursor: 'pointer' }}
                            />
                          </Box>
                        ) : null
                    }}
                    InputLabelProps={{
                      shrink: true
                    }}
                    variant="standard"
                  />
                </Box>
              </Box>
              <Box className="mt-3">
                <Select
                  sx={checkboxArray.length > 0 ? { width: 200 } : {width: 270}}
                  size="small"
                  menus={options}
                  defaultValue={selectedOption}
                  onChange={handleFilterChange}
                  label=""
                />
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                {canAddFeedback(saleDateValue) && (
                  <SubmitFeedbackButton
                    setIsOpenFeedbackModal={setIsOpenFeedbackModal}
                    houseRef={houseRef}
                    setIsButtonClickType={setIsButtonClickType}
                    setLoadingFeedbackModalData={setLoadingFeedbackModalData}
                    selectedLotsForDisputes={checkboxArray}
                  />
                )}
                {canAddDispute &&
                  <OpenDisputesButton selectedLotsForDisputes={checkboxArray} setCheckboxArray={setCheckboxArray} />
                }
              </Box>
            </Box>
            <Box className="mt-0">
              <div className="float-left">
                <button
                  className="mt-3 btn btn-link"
                  type="button"
                  onClick={() => toggleSalesResultsDetails()}
                >
                  {showSalesResultsDetails ? 'Collapse All' : 'Expand All'}
                  <FontAwesomeIcon icon={showSalesResultsDetails ? faAngleUp : faAngleDown} className="fa-lg ml-2" />
                </button>
              </div>
              <div className="float-left" style={{ margin: '22px 0 0 20px' }}>
                {checkboxArray.length} item{checkboxArray.length !== 1 && 's'} selected
              </div>
              {checkboxArray.length > 0 && (
                <div className="float-left ml-3">
                  <button
                    className="mt-3 btn btn-link"
                    type="button"
                    onClick={() => clearSelectedLots()}
                  >
                    Clear selected lots
                  </button>
                </div>
              )}
              <div className="clearfix"/>
            </Box>
            {memberData.length > 0 && (
            <Box className="data-table-wrapper" sx={{ mb: 3 }}>
              <NewSalesResultsTable
                tableData={filteredMemberData}
                catalogs={catalogs}
                memberSums={memberSums}
                showSalesResultsDetails={showSalesResultsDetails}
                setShowSalesResultsDetails={setShowSalesResultsDetails}
                expandedFlags={expandedFlags}
                setExpandedFlags={setExpandedFlags}
                checkboxArray={checkboxArray}
                setCheckboxArray={setCheckboxArray}
                sorting={sorting}
                setSorting={setSorting}
                canAddFeedback={canAddFeedback}
                setLoadingFeedbackModalData={setLoadingFeedbackModalData}
                setIsOpenFeedbackModal={setIsOpenFeedbackModal}
                currentFeedbackLotRef={currentFeedbackLotRef}
                setCurrentFeedbackLotRef={setCurrentFeedbackLotRef}
                houseRef={houseRef}
                setBidderName={setBidderName}
                setUserRef={setUserRef}
                setIsOpenSendMessageModal={setIsOpenSendMessageModal}
                multiSession={sessionCount > 1}
                currencySymbol={currencySymbol}
              />
            </Box>
            )}
            <Box sx={{ textAlign: 'right' }} mb={2}>
              {canAddDispute &&
                <OpenDisputesButton selectedLotsForDisputes={checkboxArray} setCheckboxArray={setCheckboxArray} />
              }
            </Box>
            {creditsArray.length > 0 && (
              <Box>
                <Box className="font-weight-bold mt-4 mb-2 pl-2">
                  Credits
                </Box>
                <Box className="mb-5 border-2">
                  <div className="p-2">
                    <div className="float-left font-weight-bold" style={{ width: '41%' }}>
                      Session
                    </div>
                    <div className="float-left font-weight-bold" style={{ width: '16%' }}>
                      Issue Date
                    </div>
                    <div className="float-left font-weight-bold" style={{ width: '16%' }}>
                      Amount
                    </div>
                    <div className="float-left font-weight-bold" style={{ width: '25%' }}>
                      Notes
                    </div>
                    <div className="clearfix"/>
                  </div>
                  {creditsArray.map((item, index) => (
                    <div className={`p-2 border-top ${(index % 2 === 0) ? 'credit-item-color' : ''}`}>
                      <div className="float-left" style={{ width: '41%' }}>
                        {catalogHeaderMap[item.catalogRef]}
                      </div>
                      <div className="float-left" style={{ width: '16%' }}>
                        {item.issueDate}
                      </div>
                      <div className="float-left" style={{ width: '16%' }}>
                        {item.currencySymbol}{item.creditAmount}
                      </div>
                      <div className="float-left" style={{ width: '25%' }}>
                        {item.notes}
                      </div>
                      <div className="clearfix"/>
                    </div>
                  ))}

                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <p>
            There are no transactions for this session.{' '}
            <Link to="/reports/sessionResults">Go back to sale result</Link>
          </p>
        )}
      </Container>
    </Page>
  );
};

export default SaleResults;
